import i18n, { type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// Bundler-side resource loader — globs every JSON file under src/locales/<lng>/<ns>.json
// and reshapes it into the i18next `resources` map.
interface LocaleModule {
  default: Record<string, unknown>;
}
const modules = import.meta.glob<LocaleModule>('../locales/**/*.json', { eager: true });

const resources: Resource = {};
for (const filePath in modules) {
  const match = /\.\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(filePath);
  if (!match) continue;
  const lng = match[1]!;
  const ns = match[2]!;
  resources[lng] ??= {};
  (resources[lng] as Record<string, unknown>)[ns] = modules[filePath]!.default;
}

const isBrowser = typeof window !== 'undefined';
const LANG_STORAGE_KEY = 'dcc-erp.lang';

// Honor `?lang=th|en` query param on first load by writing it into localStorage
// *before* i18next's LanguageDetector runs. This lets users share permalinks
// like `/dashboard?lang=en` without sticky behavior surprises.
if (isBrowser) {
  try {
    const params = new URLSearchParams(window.location.search);
    const qLang = params.get('lang');
    if (qLang === 'th' || qLang === 'en') {
      window.localStorage.setItem(LANG_STORAGE_KEY, qLang);
    }
  } catch {
    // ignore: SSR/sandboxed contexts without URLSearchParams / localStorage
  }
}

void i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources,
    ...(isBrowser ? {} : { lng: 'th' }),
    fallbackLng: 'th',
    supportedLngs: ['th', 'en'],
    ns: ['common', 'dashboard', 'login'],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'htmlTag', 'navigator'],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  });

// Cached Intl formatters — used by `{val, number}` and `{val, date}` interpolation.
// `addCached` (i18next v26+) creates a per-(lng, options) formatter, then runs it on each value.
i18n.services.formatter?.addCached('number', (lng, options) => {
  const fmt = new Intl.NumberFormat(lng, options as Intl.NumberFormatOptions);
  return (value: unknown) => fmt.format(Number(value));
});

i18n.services.formatter?.addCached('date', (lng, options) => {
  const fmt = new Intl.DateTimeFormat(lng, options as Intl.DateTimeFormatOptions);
  return (value: unknown) => fmt.format(value instanceof Date ? value : new Date(value as string));
});

export default i18n;
